// Isochrone service for calculating travel time/distance zones

import { GEOPORTAIL_API } from '../constants';
import type { IsoConfig, IsoPoint } from '../types';

/**
 * Parameters for isochrone API request
 */
interface IsochroneParams {
  resource: string;
  point: string;
  costType: string;
  costValue: string;
  profile: string;
  direction: string;
  geometryFormat: string;
  distanceUnit: string;
}

/**
 * Response from the Geoportail isochrone API
 */
interface IsochroneApiResponse {
  geometry?: GeoJSON.Geometry;
  error?: string;
}

/**
 * Service for calculating isochrones via Geoportail API
 */
export class IsochroneService {
  private isLoading = false;

  /**
   * Check if a calculation is in progress
   */
  get loading(): boolean {
    return this.isLoading;
  }

  /**
   * Calculate isochrone for a given point
   * @param point - Center point for isochrone
   * @param config - Isochrone configuration
   * @returns GeoJSON feature for the isochrone
   */
  async calculate(
    point: IsoPoint,
    config: IsoConfig
  ): Promise<GeoJSON.Feature<GeoJSON.Geometry> | null> {
    if (this.isLoading) {
      return null;
    }

    this.isLoading = true;

    try {
      const params = this.buildParams(point, config);
      const response = await this.fetchIsochrone(params);

      if (response.geometry) {
        return this.createFeature(response.geometry, point, config);
      }

      return null;
    } catch (error) {
      console.error("Erreur lors du calcul de l'isochrone:", error);
      throw error;
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Build API request parameters
   */
  private buildParams(point: IsoPoint, config: IsoConfig): IsochroneParams {
    const { method, graph, value } = config;

    // Convert to API units
    const costValue = method === 'time' ? value * 60 : value; // minutes -> seconds

    // Use bdtopo-valhalla for all isochrone calculations
    // It supports both car and pedestrian profiles
    const resource = 'bdtopo-valhalla';
    const profile = graph === 'Pieton' ? 'pedestrian' : 'car';

    return {
      resource,
      point: `${point.lng},${point.lat}`,
      costType: method,
      costValue: costValue.toString(),
      profile,
      direction: 'departure',
      geometryFormat: 'geojson',
      distanceUnit: 'kilometer',
    };
  }

  /**
   * Fetch isochrone from API
   */
  private async fetchIsochrone(params: IsochroneParams): Promise<IsochroneApiResponse> {
    const searchParams = new URLSearchParams(params as unknown as Record<string, string>);
    const url = `${GEOPORTAIL_API.isochrone}?${searchParams.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Create GeoJSON feature from API response
   */
  private createFeature(
    geometry: GeoJSON.Geometry,
    point: IsoPoint,
    config: IsoConfig
  ): GeoJSON.Feature<GeoJSON.Geometry> {
    return {
      type: 'Feature',
      geometry,
      properties: {
        name: point.name,
        method: config.method,
        value: config.value,
        graph: config.graph,
      },
    };
  }

  /**
   * Get display label for isochrone result
   * @param config - Isochrone configuration
   * @param name - Point name
   * @returns Display label
   */
  static getDisplayLabel(config: IsoConfig, name: string): string {
    const unitLabel = config.method === 'time' ? 'min' : 'km';
    const modeLabel = config.graph === 'Voiture' ? '🚗' : '🚶';
    return `${modeLabel} ${config.value} ${unitLabel} - ${name}`;
  }
}
